import React, {useState, useRef, useEffect} from 'react';
import {ChevronDown} from 'lucide-react';

interface PhoneInputProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  label?: string;
}

interface CountryOption {
  code: string;
  dialCode: string;
  flag: string;
  name: string;
  format: string;
  formatFunction: (input: string) => string;
}

const COUNTRIES: CountryOption[] = [
  {
    code: 'SE',
    dialCode: '+46',
    flag: '🇸🇪',
    name: 'Sweden',
    format: 'XX XXX XX XX',
    formatFunction: (input: string) => {
      const digits = input.replace(/\D/g, '');
      if (digits.length <= 2) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      if (digits.length <= 7) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
      return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`;
    },
  },
  {
    code: 'US',
    dialCode: '+1',
    flag: '🇺🇸',
    name: 'United States',
    format: '(XXX) XXX-XXXX',
    formatFunction: (input: string) => {
      const digits = input.replace(/\D/g, '');
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    },
  },
  {
    code: 'GB',
    dialCode: '+44',
    flag: '🇬🇧',
    name: 'United Kingdom',
    format: 'XXXX XXX XXX',
    formatFunction: (input: string) => {
      const digits = input.replace(/\D/g, '');
      if (digits.length <= 4) return digits;
      if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
    },
  },
  {
    code: 'DE',
    dialCode: '+49',
    flag: '🇩🇪',
    name: 'Germany',
    format: 'XXX XXXXXXX',
    formatFunction: (input: string) => {
      const digits = input.replace(/\D/g, '');
      if (digits.length <= 3) return digits;
      return `${digits.slice(0, 3)} ${digits.slice(3, 10)}`;
    },
  },
  {
    code: 'FR',
    dialCode: '+33',
    flag: '🇫🇷',
    name: 'France',
    format: 'X XX XX XX XX',
    formatFunction: (input: string) => {
      const digits = input.replace(/\D/g, '');
      if (digits.length <= 1) return digits;
      if (digits.length <= 3) return `${digits.slice(0, 1)} ${digits.slice(1)}`;
      if (digits.length <= 5) return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3)}`;
      if (digits.length <= 7) return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5)}`;
      return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`;
    },
  },
  {
    code: 'NO',
    dialCode: '+47',
    flag: '🇳🇴',
    name: 'Norway',
    format: 'XXX XX XXX',
    formatFunction: (input: string) => {
      const digits = input.replace(/\D/g, '');
      if (digits.length <= 3) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
      return `${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 8)}`;
    },
  },
  {
    code: 'DK',
    dialCode: '+45',
    flag: '🇩🇰',
    name: 'Denmark',
    format: 'XX XX XX XX',
    formatFunction: (input: string) => {
      const digits = input.replace(/\D/g, '');
      if (digits.length <= 2) return digits;
      if (digits.length <= 4) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
      return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6, 8)}`;
    },
  },
  {
    code: 'FI',
    dialCode: '+358',
    flag: '🇫🇮',
    name: 'Finland',
    format: 'XX XXX XXXX',
    formatFunction: (input: string) => {
      const digits = input.replace(/\D/g, '');
      if (digits.length <= 2) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 9)}`;
    },
  },
];

const parsePhoneNumber = (phoneNumber: string): {dialCode: string; number: string} => {
  if (!phoneNumber) return {dialCode: '+46', number: ''};

  const cleaned = phoneNumber.trim();

  // Sort by length descending to match longer codes first
  const sortedCountries = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);

  for (const country of sortedCountries) {
    if (cleaned.startsWith(country.dialCode)) {
      return {
        dialCode: country.dialCode,
        number: cleaned.slice(country.dialCode.length).trim(),
      };
    }
  }

  // Default to Sweden if no match
  return {dialCode: '+46', number: cleaned};
};

export function PhoneInput({value = '', onChange, placeholder = '', className = '', label}: PhoneInputProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const parsed = parsePhoneNumber(value);
  const [selectedDialCode, setSelectedDialCode] = useState(parsed.dialCode);
  const [phoneNumber, setPhoneNumber] = useState(parsed.number);

  const selectedCountry = COUNTRIES.find((c) => c.dialCode === selectedDialCode) || COUNTRIES[0];

  const filteredCountries = COUNTRIES.filter(
    (country) => country.name.toLowerCase().includes(searchQuery.toLowerCase()) || country.dialCode.includes(searchQuery),
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update parent when dial code or number changes
  useEffect(() => {
    const fullNumber = phoneNumber ? `${selectedDialCode} ${phoneNumber}` : '';
    onChange(fullNumber);
  }, [selectedDialCode, phoneNumber, onChange]);

  const handleCountrySelect = (country: CountryOption) => {
    setSelectedDialCode(country.dialCode);
    setIsDropdownOpen(false);
    setSearchQuery('');
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Extract only digits from input
    const digitsOnly = e.target.value.replace(/\D/g, '');

    // Apply country-specific formatting
    const formatted = selectedCountry.formatFunction(digitsOnly);
    setPhoneNumber(formatted);
  };

  return (
    <div className={className}>
      {label && <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>}
      <div className="relative flex">
        {/* Country Code Selector */}
        <div ref={dropdownRef} className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="h-full px-3 py-2 border border-r-0 border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors flex items-center space-x-2"
          >
            <span className="text-xl" role="img" aria-label={selectedCountry.name}>
              {selectedCountry.flag}
            </span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedCountry.dialCode}</span>
            <ChevronDown size={16} className="text-gray-600 dark:text-gray-400" />
          </button>

          {/* Dropdown */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-80 overflow-hidden flex flex-col">
              {/* Search */}
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search country..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              {/* Country List */}
              <div className="overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3 ${
                        country.dialCode === selectedDialCode ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                      <span className="text-xl" role="img" aria-label={country.name}>
                        {country.flag}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{country.name}</span>
                          <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">{country.dialCode}</span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-500">{country.format}</span>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-4 text-sm text-center text-gray-500 dark:text-gray-400">No countries found</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          placeholder={placeholder || selectedCountry.format}
          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-r-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>
  );
}
